/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { USER_PROPERTY_BY_STATE_KEY } from '../constants.tsx';
import type {
	State,
	Props,
	GlobalFieldsTourSteps,
	ProjectFieldsTourSteps,
	CollectionsTourSteps,
	PrioritizationViewTourSteps,
} from '../types.tsx';
import { setCollectionsTourSpotlightStep } from './collections/tour-spotlights/index.tsx';
import {
	loadCollectionsTourDismissed,
	setCollectionsTourDismissed,
} from './collections/tour/index.tsx';
import {
	loadCrossProjectViewFieldsDismissed,
	setCrossProjectViewFieldsDismissed,
} from './cross-project-view/fields-section/index.tsx';
import {
	loadRoadmapIntroductionDismissed,
	loadRoadmapIntroductionPrerequisiteDismissalsMet,
	setRoadmapIntroductionDismissed,
} from './collections/introduction/index.tsx';
import {
	loadGoToProjectSpotlightDismissed,
	setGoToProjectSpotlightDismissed,
} from './global-fields/go-to-project-spotlight/index.tsx';
import { setGlobalFieldsTourSpotlightStep } from './global-fields/tour-spotlights/index.tsx';
import {
	loadGlobalFieldsTourDismissed,
	setGlobalFieldsTourDismissed,
} from './global-fields/tour/index.tsx';
import {
	loadExploreSectionMessageDismissed,
	setExploreSectionMessageDismissed,
} from './project-fields/explore-section-message/index.tsx';
import {
	loadProjectFieldsPageViewed,
	setProjectFieldsPageViewed,
} from './project-fields/page-viewed/index.tsx';
import {
	loadSeenPulsatingFieldsButton,
	setSeenPulsatingFieldsButton,
} from './project-fields/pulsating-fields-button/index.tsx';
import { setProjectFieldsTourSpotlightStep } from './project-fields/tour-spotlights/index.tsx';
import {
	loadProjectFieldsTourDismissed,
	setProjectFieldsTourDismissed,
} from './project-fields/tour/index.tsx';
import {
	loadTimelineSectionMessageDismissed,
	setTimelineSectionMessageDismissed,
} from './timeline/spotlight/index.tsx';
import { loadGlobalViewsSectionMessageDismissed } from './global-views/section-message/loadGlobalViewsSectionMessageDismissed.tsx';
import { setGlobalViewsSectionMessageDismissed } from './global-views/section-message/setGlobalViewsSectionMessageDismissed.tsx';
import {
	loadWeeklyModeSpotlightSeen,
	setWeeklyModeSpotlightSeen,
} from './timeline/weekly-mode-spotlight/loadWeeklyModeSpotlightSeen.tsx';
import {
	loadAllViewsSpotlightDismissed,
	setAllViewsSpotlightDismissed,
} from './view-spotlights/all-views-spotlight/index.tsx';

import {
	loadPrioritizationViewTourDismissed,
	setPrioritizationViewTourDismissed,
	setPrioritizationViewTourSpotlightStep,
} from './view-spotlights/impact-assessment-view-tour/index.tsx';

import {
	loadRoadmapViewSpotlightDismissed,
	setRoadmapViewSpotlightDismissed,
	loadRoadmapColumnConfigSpotlightDismissed,
	setRoadmapColumnConfigSpotlightDismissed,
} from './view-spotlights/roadmap-view-spotlights/index.tsx';
import { setBlueprintsTourDismissed, loadBlueprintsState } from './blueprints/index.tsx';
import {
	loadPremiumLastVisit,
	loadPremiumModalDismissed,
	setPremiumLastVisit,
	setPremiumModalDismissed,
	unlockOnboardingFlow,
} from './premium/index.tsx';
import { loadProductUsageStartTimestamp } from './product-start-timestamp/index.tsx';
import {
	loadCustomDatesCustomizeSpotlightDismissed,
	setCustomDatesCustomizeSpotlightDismissed,
} from './timeline/custom-dates-spotlight/index.tsx';
import { loadImportState, setSuccessNotificationViewed } from './imports/index.tsx';
import {
	loadUnifiedShareDialogSpotlightState,
	setUnifiedShareDialogSpotlightDismissed,
} from './unified-share-dialog/index.tsx';
import {
	loadTreeViewOnboardingSpotlightDismissed,
	setTreeViewOnboardingSpotlightDismissed,
} from './view-spotlights/tree-view-onboarding-spotlight/loadTreeViewOnboardingSpotlightDismissed.tsx';
import { loadCardCoverSpotlightState } from './card-cover-spotlight/loadCardCoverSpotlightState.tsx';
import { setCardCoverSpotlightDismissed } from './card-cover-spotlight/setCardCoverSpotlightDismissed.tsx';
import {
	loadStartWithTemplateSpotlightDismissed,
	setStartWithTemplateSpotlightDismissed,
} from './view-spotlights/start-with-template-spotlight/loadStartWithTemplateSpotlightDismissed.tsx';

export type Actions = {
	loadProductUsageStartTimestamp: () => Action<State, Props, Promise<string | undefined>>;
	loadGoToProjectSpotlightDismissed: () => Action<State, Props>;
	setGoToProjectSpotlightDismissed: () => Action<State, Props>;
	setGlobalFieldsTourSpotlightStep: (step: GlobalFieldsTourSteps | null) => Action<State>;
	loadGlobalFieldsTourDismissed: () => Action<State, Props>;
	setGlobalFieldsTourDismissed: () => Action<State, Props>;
	loadExploreSectionMessageDismissed: () => Action<State, Props>;
	setExploreSectionMessageDismissed: () => Action<State, Props>;
	loadProjectFieldsPageViewed: () => Action<State, Props>;
	setProjectFieldsPageViewed: () => Action<State, Props>;
	loadSeenPulsatingFieldsButton: () => Action<State, Props>;
	setSeenPulsatingFieldsButton: () => Action<State, Props>;
	loadProjectFieldsTourDismissed: () => Action<State, Props>;
	setProjectFieldsTourDismissed: () => Action<State, Props>;
	setProjectFieldsTourSpotlightStep: (step: ProjectFieldsTourSteps | null) => Action<State>;
	loadCollectionsTourDismissed: () => Action<State, Props, Promise<boolean | undefined>>;
	loadRoadmapIntroductionPrerequisiteDismissalsMet: () => Action<
		State,
		Props,
		Promise<boolean | undefined>
	>;
	setCollectionsTourDismissed: () => Action<State, Props>;
	setCollectionsTourSpotlightStep: (step: CollectionsTourSteps | null) => Action<State>;
	loadRoadmapIntroductionDismissed: () => Action<State, Props, Promise<boolean | undefined>>;
	setRoadmapIntroductionDismissed: () => Action<State, Props>;
	loadTimelineSectionMessageDismissed: () => Action<State, Props>;
	setTimelineSectionMessageDismissed: () => Action<State, Props>;
	loadWeeklyModeSpotlightSeen: () => Action<State, Props>;
	setWeeklyModeSpotlightSeen: () => Action<State, Props>;
	loadCrossProjectViewFieldsDismissed: (
		viewKindStateKey: keyof typeof USER_PROPERTY_BY_STATE_KEY,
	) => Action<State, Props, Promise<boolean | undefined>>;
	setCrossProjectViewFieldsDismissed: (
		viewKindStateKey: keyof typeof USER_PROPERTY_BY_STATE_KEY,
	) => Action<State, Props>;
	loadAllViewsSpotlightDismissed: () => Action<State, Props>;
	setAllViewsSpotlightDismissed: () => Action<State, Props>;
	loadRoadmapViewSpotlightDismissed: () => Action<State, Props>;
	setRoadmapViewSpotlightDismissed: () => Action<State, Props>;
	loadRoadmapColumnConfigSpotlightDismissed: () => Action<State, Props>;
	setRoadmapColumnConfigSpotlightDismissed: () => Action<State, Props>;
	loadPrioritizationViewTourDismissed: () => Action<State, Props>;
	setPrioritizationViewTourDismissed: () => Action<State, Props>;
	setPrioritizationViewTourSpotlightStep: (
		step: PrioritizationViewTourSteps | null,
	) => Action<State>;
	setBlueprintsTourDismissed: (tourDismissed: boolean) => Action<State, Props>;
	loadBlueprintsState: () => Action<State, Props, Promise<void>>;
	loadPremiumLastVisit: (
		isPremiumEdition: boolean,
	) => Action<State, Props, Promise<boolean | undefined>>;
	loadPremiumModalDismissed: () => Action<State, Props, Promise<boolean | undefined>>;
	setPremiumLastVisit: (isPremium: boolean) => Action<State, Props>;
	setPremiumModalDismissed: () => Action<State, Props>;
	loadCustomDatesCustomizeSpotlightDismissed: () => Action<
		State,
		Props,
		Promise<boolean | undefined>
	>;
	setCustomDatesCustomizeSpotlightDismissed: () => Action<State, Props>;
	unlockOnboardingFlow: () => Action<State, Props>;
	loadImportState: () => Action<State, Props, Promise<void>>;
	setSuccessNotificationViewed: (successNotificationViewed: boolean) => Action<State, Props>;
	loadUnifiedShareDialogSpotlightState: () => Action<State, Props, Promise<void>>;
	setUnifiedShareDialogSpotlightDismissed: (spotlightDismissed: boolean) => Action<State, Props>;
	loadTreeViewOnboardingSpotlightDismissed: () => Action<State, Props>;
	setTreeViewOnboardingSpotlightDismissed: () => Action<State, Props>;
	loadCardCoverSpotlightState: () => Action<State, Props, Promise<void>>;
	setCardCoverSpotlightDismissed: (spotlightDismissed: boolean) => Action<State, Props>;
	loadGlobalViewsSectionMessageDismissed: () => Action<State, Props>;
	setGlobalViewsSectionMessageDismissed: () => Action<State, Props>;
	loadStartWithTemplateSpotlightDismissed: () => Action<State, Props>;
	setStartWithTemplateSpotlightDismissed: () => Action<State, Props>;
};

export const actions: Actions = {
	loadProductUsageStartTimestamp,
	loadGoToProjectSpotlightDismissed,
	setGoToProjectSpotlightDismissed,
	setGlobalFieldsTourSpotlightStep,
	loadGlobalFieldsTourDismissed,
	setGlobalFieldsTourDismissed,
	loadExploreSectionMessageDismissed,
	setExploreSectionMessageDismissed,
	loadProjectFieldsPageViewed,
	setProjectFieldsPageViewed,
	loadSeenPulsatingFieldsButton,
	setSeenPulsatingFieldsButton,
	loadProjectFieldsTourDismissed,
	setProjectFieldsTourDismissed,
	setProjectFieldsTourSpotlightStep,
	loadCollectionsTourDismissed,
	loadRoadmapIntroductionPrerequisiteDismissalsMet,
	setCollectionsTourDismissed,
	setCollectionsTourSpotlightStep,
	loadRoadmapIntroductionDismissed,
	setRoadmapIntroductionDismissed,
	loadTimelineSectionMessageDismissed,
	setTimelineSectionMessageDismissed,
	loadWeeklyModeSpotlightSeen,
	setWeeklyModeSpotlightSeen,
	loadCrossProjectViewFieldsDismissed,
	setCrossProjectViewFieldsDismissed,
	loadAllViewsSpotlightDismissed,
	setAllViewsSpotlightDismissed,
	loadRoadmapViewSpotlightDismissed,
	setRoadmapViewSpotlightDismissed,
	loadRoadmapColumnConfigSpotlightDismissed,
	setRoadmapColumnConfigSpotlightDismissed,
	loadPrioritizationViewTourDismissed,
	setPrioritizationViewTourDismissed,
	setPrioritizationViewTourSpotlightStep,
	setBlueprintsTourDismissed,
	loadBlueprintsState,
	loadPremiumLastVisit,
	loadPremiumModalDismissed,
	setPremiumLastVisit,
	setPremiumModalDismissed,
	loadCustomDatesCustomizeSpotlightDismissed,
	setCustomDatesCustomizeSpotlightDismissed,
	unlockOnboardingFlow,
	loadImportState,
	setSuccessNotificationViewed,
	loadUnifiedShareDialogSpotlightState,
	setUnifiedShareDialogSpotlightDismissed,
	loadTreeViewOnboardingSpotlightDismissed,
	setTreeViewOnboardingSpotlightDismissed,
	loadCardCoverSpotlightState,
	setCardCoverSpotlightDismissed,
	loadGlobalViewsSectionMessageDismissed,
	setGlobalViewsSectionMessageDismissed,
	loadStartWithTemplateSpotlightDismissed,
	setStartWithTemplateSpotlightDismissed,
};
